import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { ChannelIdSchema } from '../primitives/ids';

import { SubscriptionSummarySchema } from './subscriptions';

/** A Telegram channel the trader is a member of (FXM-01 §4). No statistics or ranking (FXM-01 §8). */
export const ChannelSchema = z.object({
  id: ChannelIdSchema,
  title: z.string().min(1).max(256),
  hasPhoto: z.boolean().describe('When true, GET /channels/{channelId}/photo returns the image.'),
  recommendedByAffiliate: z
    .boolean()
    .describe("Recommended by the trader's affiliate; listed first (FXM-01 §4)."),
  accessible: z
    .boolean()
    .describe(
      'False when the channel became inaccessible; copying stopped (FXM-01 §5, §6 case 3).',
    ),
  subscription: SubscriptionSummarySchema.nullable().describe(
    'null when the channel is not selected.',
  ),
});
/** Channel. */
export type Channel = z.infer<typeof ChannelSchema>;

/** Response of GET /channels. Not paged: 300 channels are searched on the client (FXM-01 §6 case 1). */
export const ChannelListResponseSchema = z.object({
  channels: z
    .array(ChannelSchema)
    .describe('Recommended channels first, then by title (technical spec §6).'),
  affiliateChannelUrl: z
    .url()
    .nullable()
    .describe(
      "Telegram link of the affiliate's channel for the E3 empty state; null without an affiliate (S5 R7, Q75). Provisional.",
    ),
});
/** Channel list. */
export type ChannelListResponse = z.infer<typeof ChannelListResponseSchema>;

/** Path parameters of GET /channels/{channelId}/photo. */
export const ChannelPathParamsSchema = z.strictObject({ channelId: ChannelIdSchema });

/** GET /channels. */
export const listChannelsRoute = defineRoute({
  operationId: 'listChannels',
  method: 'GET',
  path: '/channels',
  tag: 'Channels',
  summary: 'List the channels the trader is a member of',
  description:
    "Serves E3 and E12. Needs the trader's Telegram session (FXM-01 §4): 409 when it is lost, 503 when Telegram cannot be reached (C1 §5.1 row 4).",
  source: 'technical spec §6; FXM-01 §4, §5; US-04, US-07',
  auth: 'bearer',
  provisional: {
    questions: ['Q30', 'Q36', 'Q75'],
    note: 'Channels tab reuse, channel = master account and the affiliate channel link are open.',
  },
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'ChannelListResponse', schema: ChannelListResponseSchema },
  },
  errors: [
    { status: 409, reasonCode: 'TELEGRAM_SESSION_LOST' },
    { status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' },
  ],
});

/** GET /channels/{channelId}/photo. Fetched with `fetch` + blob URL: `<img>` cannot send a bearer token. */
export const getChannelPhotoRoute = defineRoute({
  operationId: 'getChannelPhoto',
  method: 'GET',
  path: '/channels/{channelId}/photo',
  tag: 'Channels',
  summary: 'Get a channel image',
  description:
    'Served from a cache; the API never touches a Telegram session for it (system design §Mini App).',
  source: 'technical spec §6; system design §Mini App; US-04',
  auth: 'bearer',
  params: ChannelPathParamsSchema,
  success: { kind: 'binary', status: 200, contentType: 'image/jpeg' },
});
